import { useEffect, useState } from "react";
import BabyBirthPosition from "./BabyBirthPosition";
import { BabyBodyPositions, Visibility } from "../helpers/Enums";
import CervixDilation from "./CervixDilation";
import {
  babyBodyPositionState,
  babyBodyRotationState,
  babyHeadPositionState,
  cervixOpeningState,
  progressPlayerModeActiveState,
} from "../helpers/StateManager";
import Utilities from "../helpers/SVGManipulation";
import Dictionaries from "../helpers/Dictionaries";
import SVGImagePositioning from "../helpers/SVGImagePositioning";
import SVGManipulation from "../helpers/SVGManipulation";

// Component for visualizing and interacting with baby rotation and cervix opening during childbirth.
// Uses React state to manage baby position, rotation, and cervix opening state.
// Handles SVG manipulation for rotation visualization and interactive touch/click events.
// Calculates angle based on user input and updates rotation angle accordingly.
// Provides functions to determine rotation labels, visibility, and initial setup on component mount.
// Integrates with helper functions for SVG positioning and dictionary lookup for labels.
// Effect hook initializes baby head position and handles updates on body position changes.
// Renders SVG elements for visualization and interaction, including rotation and cervix dilation.
const RotationAndOpening = () => {
  // Variables
  const [babyHeadPosition] = babyHeadPositionState();
  const [babyBodyPosition] = babyBodyPositionState();
  const [babyBodyRotation, setBabyBodyRotation] = babyBodyRotationState();
  const [cervixOpening] = cervixOpeningState();
  // Is Progress player active?
  const [isPlayerInProgress] = progressPlayerModeActiveState();

  // Toggles the draggable attribute of the arrow element based on true/false values.
  const [arrowDraggable, setArrowDraggable] = useState(false);
  const [currentRotationInAngles, setCurrentRotationInAngles] = useState(0);

  // Initial coordinates for the baby head position, starting at X:137 Y:98 pixels.
  const [babyHeadPositionX, setBabyHeadPositionX] = useState(137);
  const [babyHeadPositionY, setBabyHeadPositionY] = useState(98);

  // The rotation center is hardcoded.
  const rotationCenter = [233.5, 200];

  // Defines the SVG element used for visualizing rotation.
  const rotationOpeningSVGElement = document.querySelector<SVGSVGElement>(
    "#RotationOpeningSVG"
  );

  // Defines the clickable/touchable area using coordinates.
  const clickableTouchableAreaCoordinates =
    SVGManipulation.getSvgElementCoordinates(rotationOpeningSVGElement!);

  // Converts SVG center to pixel coordinates if rotation opening SVG element exists; otherwise returns origin.
  const getSvgCenterToPixel = () => {
    if (rotationOpeningSVGElement) {
      return Utilities.svgLocationToPixel(
        rotationOpeningSVGElement,
        rotationCenter[0],
        rotationCenter[1]
      );
    }
    return { x: 0, y: 0 };
  };

  // Calculates the angle based on the clicked/touched point's coordinates.
  const calculateAngle = (
    clientXCoordinate: number,
    clientYCoordinate: number
  ) => {
    var center = getSvgCenterToPixel();
    const deltaX = clientXCoordinate - center.x;
    const deltaY = clientYCoordinate - center.y;
    let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
    angle = angle < 0 ? angle + 360 : angle;
    return angle;
  };

  // Retrieves the updated angle after a mouse/touch event ends, rounding it to the
  // nearest rotation circle level in the SVG image.
  const getNewAngleValueAfterTouchMouseEvents = (
    clientXCoordinate: number,
    clientYCoordinate: number
  ) => {
    const currentAngleValue = calculateAngle(
      clientXCoordinate,
      clientYCoordinate
    );
    const newRoundedAngleValue = Math.round(currentAngleValue / 30) * 30;
    return newRoundedAngleValue;
  };

  // Click / Touch shared actions

  // Start of click/touch event
  const startClickOrTouch = () => {
    setArrowDraggable(true);
  };

  // Handling mouse movement or touch drag events
  const moveWhileClickOrTouch = (
    clientXCoordinate: number,
    clientYCoordinate: number
  ) => {
    const angle = calculateAngle(clientXCoordinate, clientYCoordinate);
    setCurrentRotationInAngles(angle);
  };

  // Handling mouse release or touch drag end
  const endClickOrTouch = (
    event: React.MouseEvent | React.TouchEvent,
    clientXCoordinate: number,
    clientYCoordinate: number
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setArrowDraggable(false);

    // Check if click/touch event is outside clickable/touchable area to cancel dragging and reset arrow
    if (
      clientXCoordinate < clickableTouchableAreaCoordinates!.left ||
      clientXCoordinate > clickableTouchableAreaCoordinates!.right ||
      clientYCoordinate < clickableTouchableAreaCoordinates!.top ||
      clientYCoordinate > clickableTouchableAreaCoordinates!.bottom
    ) {
      cancelClickOrTouch(event);
      return;
    }

    // Calculate rounded angle value based on touch or mouse events coordinates
    const roundedAngleValue = getNewAngleValueAfterTouchMouseEvents(
      clientXCoordinate,
      clientYCoordinate
    );
    setCurrentRotationInAngles(roundedAngleValue);
    // Updating the current rotation of the baby in the parent component (Visualizations).
    // This change will reflect in the cervix descent component, adjusting the baby's orientation.
    setBabyBodyRotation(getRotationValueByAngle(roundedAngleValue));
  };

  // Mouse out of clickable area or touch cancelled (interrupted or dragged outside the draggable area)
  const cancelClickOrTouch = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setArrowDraggable(false);

    // If the mouse moves or clicks outside the rotation visualization, the drag functionality will be cancelled,
    // resetting the arrow level to its previous value before the drag/drop operation.
    var angle = SVGImagePositioning.getAngleByBodyPositionAndBodyRotation(
      babyBodyPosition,
      babyBodyRotation
    );
    setCurrentRotationInAngles(angle);
  };

  // Retrieves the rotation label based on the current angle and baby's body position,
  // using predefined dictionaries for different body positions (Breech or Cephalic).
  const getRotationLabelByAngle = () => {
    // Determines rotation labels based on the baby's body position.
    if (babyBodyPosition === BabyBodyPositions.Breech) {
      return currentRotationInAngles > 0
        ? Dictionaries.rotationLabelsByAngleAndBreechPosition[
            currentRotationInAngles
          ]
        : Dictionaries.rotationLabelsByAngleAndBreechPosition[
            360 - Math.abs(currentRotationInAngles)
          ];
    }
    // Default position (Cephalic - Hodeleie)
    return currentRotationInAngles > 0
      ? Dictionaries.rotationLabelsByAngleAndCephalicPosition[
          currentRotationInAngles
        ]
      : Dictionaries.rotationLabelsByAngleAndCephalicPosition[
          360 - Math.abs(currentRotationInAngles)
        ];
  };

  // Retrieves rotation values corresponding to the rounded angle based on baby's body position.
  const getRotationValueByAngle = (roundedAngleValue: number) => {
    // Breech position
    if (babyBodyPosition === BabyBodyPositions.Breech) {
      return roundedAngleValue > 0
        ? Dictionaries.rotationLabelInternalValuesByAngleAndBreechPosition[
            roundedAngleValue
          ]
        : Dictionaries.rotationLabelInternalValuesByAngleAndBreechPosition[
            360 - Math.abs(roundedAngleValue)
          ];
    }

    // Default position (Cephalic - Hodeleie)
    return roundedAngleValue > 0
      ? Dictionaries.rotationLabelInternalValuesByAngleAndCephalicPosition[
          roundedAngleValue
        ]
      : Dictionaries.rotationLabelInternalValuesByAngleAndCephalicPosition[
          360 - Math.abs(roundedAngleValue)
        ];
  };

  // Determines if the top rotation label should be visible based on the current rotation angle.
  const getTopRotationLabelVisibility = () => {
    return (currentRotationInAngles >= 0 && currentRotationInAngles <= 90) ||
      (currentRotationInAngles >= 270 && currentRotationInAngles <= 360) ||
      (currentRotationInAngles < 0 && currentRotationInAngles >= -90)
      ? "visible"
      : "hidden";
  };

  // Determines if the bottom rotation label should be visible based on the current rotation angle.
  const getBottomRotationLabelVisibility = () => {
    return (currentRotationInAngles > 90 && currentRotationInAngles < 270) ||
      (currentRotationInAngles < -90 && currentRotationInAngles > -180)
      ? "visible"
      : "hidden";
  };

  useEffect(() => {
    // Calculates the initial coordinates for the baby's head position based on the default image.
    const babyHeadPositionCoordinates =
      SVGImagePositioning.getBabyHeadImagesInitialPosition(babyHeadPosition);
    if (babyHeadPositionCoordinates !== null) {
      setBabyHeadPositionX(babyHeadPositionCoordinates.posX);
      setBabyHeadPositionY(babyHeadPositionCoordinates.posY);
    }

    // Sets the current rotation angle based on the baby's body position and rotation.
    var angle = SVGImagePositioning.getAngleByBodyPositionAndBodyRotation(
      babyBodyPosition,
      babyBodyRotation
    );
    setCurrentRotationInAngles(angle);
  }, [babyBodyPosition, babyBodyRotation, babyHeadPosition]);

  return (
    <div className="svg-clickable-area rotation-area">
      <svg
        width="468"
        height="398"
        viewBox="0 0 468 398"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        id="RotationOpeningSVG"
      >
        <g>
          {/* Start: Pelvis / Legs lines and filling */}
          <path
            d="M327.361 40.7949L307.11 75.12C306.377 75.0568 264.636 60.5357 252.163 56.5442C246.736 54.8103 227.652 59.8494 227.652 59.8494C227.652 59.8494 211.516 55.9572 205.321 56.6435C196.366 57.6369 178.15 66.1888 162.675 72.8262C150.157 78.1904 139.059 81.9832 139.059 81.9832L96.2682 40.7949H-25.7725V303.791C-24.7504 304.722 9.03168 323.812 46.8206 337.855C71.359 346.975 97.2721 353.739 114.195 355.103C131.959 356.539 154.779 357.695 169.775 355.816C207.048 351.157 227.715 341.62 233.196 340.762C234.038 340.636 234.852 340.419 235.639 340.735C286.696 361.28 315.151 360.277 349.846 356.945C412.255 350.949 485.255 298.129 484.034 296.395V40.7949H327.361ZM340.503 232.008C313.26 255.541 266.508 315.992 252.055 308.216C247.234 305.625 242.277 298.201 234.264 298.888C227.019 299.511 223.184 305.227 219.792 308.821C214.971 313.942 154.001 266.902 131.018 242.88C122.118 233.579 123.493 219.726 127.545 211.906L204.416 126.413C208.966 119.523 237.149 118.349 249.296 126.802L270.307 142.732L334.832 210.533C334.862 210.571 334.892 210.609 334.922 210.647C339.371 216.303 347.187 226.24 340.503 232.017V232.008Z"
            fill="white"
          />
          <path
            d="M340.503 232.008C313.26 255.541 266.508 315.992 252.055 308.216C247.234 305.625 242.277 298.201 234.264 298.888C227.019 299.511 223.184 305.227 219.792 308.821C214.971 313.942 154.001 266.902 131.018 242.88C122.118 233.579 123.493 219.726 127.545 211.906L204.416 126.413C208.966 119.523 237.149 118.349 249.296 126.802L270.307 142.732L334.832 210.533L334.922 210.647C339.371 216.303 347.187 226.24 340.503 232.017V232.008Z"
            fill="white"
          />
          <path
            d="M413.033 143.77C410.719 149.125 406.738 156.579 404.249 159.064C402.35 161.313 400.682 163.275 398.276 165.027C397.354 165.65 396.43 166.04 395.29 166.148C394.958 166.184 395.111 166.347 395.29 166.148C395.191 166.166 395.417 166.112 395.29 166.148C399.188 165.949 400.796 168.369 400.995 172.189C401.655 176.56 401.709 180.949 401.673 185.374C401.646 194.594 399.547 203.895 395.052 212.005C387.554 225.894 375.371 233.389 359.416 231.953C357.67 231.881 355.924 232.314 354.713 233.588C348.689 239.123 326.176 259.812 319.755 265.7C311.135 273.665 302.117 281.296 294.936 290.625C289.509 297.515 284.987 305.128 281.83 313.309C279.198 320.516 277.597 327.885 269.837 331.569C265.748 333.574 260.991 333.736 256.595 333.736L247.749 333.845C235.964 333.935 224.17 333.881 212.375 333.601L210.169 333.547C206.216 333.8 202.68 331.262 200.31 328.309C195.742 322.53 191.473 316.506 187.123 310.582C166.564 281.739 137.946 259.948 108.47 240.785C98.7916 234.148 88.3631 228.206 79.6892 220.25C71.0695 212.14 64.9187 201.033 63.8152 189.194C62.8746 180.38 64.0886 171.564 67.797 163.536C69.018 160.854 70.7834 158.07 72.3539 155.465C69.9299 160.811 66.8016 164.53 65.8063 171.487C64.332 177.176 63.7342 183.369 64.3945 189.194C65.6065 200.924 71.5218 211.797 80.1414 219.762C88.8515 227.637 99.2348 233.488 108.94 240.063C138.634 259.099 167.414 280.881 188.108 309.842C192.486 315.766 196.728 321.753 201.305 327.505C203.512 330.16 206.587 332.517 210.196 332.237L212.403 332.292C224.179 332.544 235.955 332.581 247.731 332.472L256.568 332.382C260.955 332.382 265.296 332.274 269.267 330.368C276.575 326.936 278.067 319.739 280.636 312.831C283.847 304.541 288.46 296.811 293.959 289.848C301.204 280.474 310.294 272.798 318.959 264.815C325.507 258.837 347.848 238.491 354.052 232.847C355.418 231.411 357.534 230.879 359.47 230.987C374.421 232.495 386.586 225.569 393.849 212.574C398.733 204.383 401.04 194.847 401.166 185.347C401.239 180.949 401.131 176.542 400.516 172.189C400.348 169.241 399.086 166.527 395.36 166.373C395.124 166.355 394.765 166.373 394.532 166.373C394.254 166.373 394.225 166.148 394.555 166.121C395.632 166.031 396.386 165.614 397.281 165.027C399.669 163.329 404.747 158.07 406.24 155.585C409.177 150.698 412.213 144.156 413.015 143.752L413.033 143.77Z"
            fill="#2E7FA1"
          />
          <path
            d="M426.483 59.208C425.506 60.8606 425.153 62.9557 423.516 64.1567L418.668 67.3174C409.108 74.0452 397.784 77.4316 386.858 81.1251C379.739 83.735 372.124 85.9474 366.218 90.833C360.411 95.6011 356.721 103.006 356.802 110.529C356.847 118.99 362.229 123.957 368.443 128.806C377.397 135.977 387.934 141.386 399.321 143.228C406.765 144.547 415.032 144.574 421.318 139.797C422.865 138.677 424.276 137.349 425.533 135.895C426.817 134.505 427.74 132.563 429.694 132.003C427.749 132.635 426.926 134.577 425.687 136.022C424.466 137.521 423.082 138.894 421.535 140.076C415.276 145.116 406.792 145.323 399.195 144.068C387.59 142.424 376.782 137.015 367.683 129.773C361.116 124.734 355.545 119.442 355.454 110.547C355.418 102.591 359.307 94.8696 365.44 89.8848C371.545 84.8999 379.224 82.6513 386.541 80.1408C397.576 76.6099 408.755 73.4401 418.496 67.0194L423.426 63.9852C425.026 62.9015 425.488 60.7793 426.501 59.208H426.483Z"
            fill="#2E7FA1"
          />
          <path
            d="M327.602 40.7949C323.487 48.2903 316.48 60.0751 312.229 67.4892C310.728 70.1532 309.154 72.7811 307.626 75.427L307.201 74.524C311.397 75.1652 315.522 76.1314 319.737 76.4926C326.755 77.0435 333.901 77.3686 340.928 77.9466C346.59 78.3529 352.406 79.0934 357.607 81.4594L361.55 83.0217C364.173 84.0873 366.923 84.6924 369.709 85.171C371.093 85.3606 372.522 85.7038 373.915 85.4419C371.12 85.9386 368.234 85.18 365.467 84.7556C359.913 83.6719 354.857 80.6738 349.268 79.9333C339.445 78.7232 329.514 78.6509 319.637 77.8201C316.67 77.6124 310.004 76.2217 307.02 75.7251L306.151 75.5625L306.595 74.822C312.655 64.6265 320.275 50.8459 326.607 40.7949H327.602Z"
            fill="#2E7FA1"
          />
          <path
            d="M139.05 81.9837C153.856 77.6851 174.152 68.492 187.457 62.0171C198.483 56.653 209.246 54.3863 221.257 58.7661C225.201 60.202 230.474 60.193 234.499 58.8745C248.618 54.2689 261.208 57.4657 273.988 63.7329C282.662 67.9863 298.002 71.7611 307.11 75.1205"
            stroke="#2E7FA1"
            strokeWidth="1.5"
            strokeMiterlimit="10"
          />
          <path
            d="M85.5771 92.0794C90.1176 92.3322 94.649 91.4833 99.1262 90.806C103.024 90.2642 108.596 88.9096 112.467 87.9705C120.01 86.1644 128.178 84.3402 135.757 82.5883C137.033 82.3896 136.889 82.1572 137.974 81.5431C130.011 70.722 119.061 62.6598 108.985 53.1306C105.53 50.0241 99.3049 44.2265 96.1664 40.7949C99.522 44.0098 106.005 49.2894 109.605 52.2243C120.056 61.6659 132.002 71.1076 140.017 82.2451C139.032 82.6605 137.982 83.5003 136.888 83.69L136.056 83.8706C128.449 85.5232 120.282 87.3835 112.684 88.9728C108.768 89.8037 103.169 91.0318 99.2257 91.4472C94.7033 91.9258 90.1176 92.6122 85.5771 92.0703V92.0794Z"
            fill="#2E7FA1"
          />
          <path
            d="M45.4009 143.228C46.3867 143.499 47.0199 144.411 47.6982 145.116C49.6519 147.391 51.705 149.622 54.2014 151.302C60.8945 155.907 69.496 156.034 77.2112 154.462C89.0779 152.385 99.7778 146.335 109.211 139.002C111.536 137.232 113.743 135.173 115.371 132.744C120.581 125.203 118.148 115.143 112.847 108.298C105.756 98.2471 93.3289 94.8697 82.0682 91.6367C72.4808 88.7018 62.5678 86.3178 53.514 81.9108C50.0227 79.8429 47.1555 76.9711 44.1165 74.3433C42.389 73.1693 41.7468 71.1194 40.8242 69.3584C42.1809 73.3228 45.9164 75.3727 48.865 78.0458C51.9312 80.6917 55.4224 82.534 59.2936 83.717C70.6266 87.8439 82.4752 90.2731 93.9349 94.075C105.693 97.8407 116.212 106.095 119.161 118.557C121.151 127.262 116.981 135.001 109.962 139.977C100.42 147.373 89.4487 153.415 77.374 155.302C69.5232 156.801 60.6684 156.467 54.0024 151.6C51.5241 149.83 49.5162 147.554 47.6349 145.188C46.9746 144.465 46.3867 143.535 45.4099 143.228H45.4009Z"
            fill="#2E7FA1"
          />
          <path
            d="M-25.7722 304.406C-21.8287 306.663 -14.9547 309.869 -10.7489 311.892C4.05734 318.981 19.8585 326.919 35.0175 332.906C91.5019 356.286 156.217 365.163 215.125 345.585C219.114 344.302 223.067 342.93 226.992 341.449C230.266 340.139 234.164 338.631 237.62 340.239C250.979 346.316 264.917 350.967 279.171 354.462L283.241 355.491C285.213 355.97 287.348 356.205 289.365 356.421C302.597 357.451 315.902 357.352 329.179 357.153C338.957 356.936 348.752 356.683 358.493 355.78C370.975 354.417 382.987 350.308 394.736 345.901C425.976 333.701 455.851 317.689 482.913 297.858C456.014 317.969 426.284 334.351 395.043 346.705C382.01 351.698 368.479 356.322 354.405 357.225C332.734 358.607 310.945 359.067 289.247 357.668C287.149 357.451 284.96 357.207 282.925 356.719L278.845 355.699C264.528 352.222 250.49 347.563 237.059 341.485C233.875 340.067 230.529 341.557 227.444 342.722C223.501 344.203 219.521 345.576 215.514 346.858C156.316 366.445 91.33 357.415 34.7552 333.529C19.6324 327.433 3.85835 319.234 -10.8303 312.054L-18.3646 308.343L-22.1272 306.483C-23.3573 305.814 -24.515 305.02 -25.7812 304.415L-25.7722 304.406Z"
            fill="#2E7FA1"
          />
          {/* End: Pelvis / Legs lines and filling  */}

          {/* Start: Inner circle with dashed lines */}
          <g transform="translate(115, 81)">
            <path
              d="M123.518 237.73L123.258 231.004C140.012 230.349 156.014 226.067 170.824 218.282L173.961 224.248C158.247 232.505 141.282 237.047 123.518 237.74V237.73ZM114.03 237.72H113.895C96.1028 236.989 79.1474 232.447 63.5008 224.229L66.6378 218.263C81.3703 226.009 97.3345 230.292 114.097 230.984H114.261L113.905 237.72L114.069 234.574L114.03 237.72ZM182.236 219.456L178.637 213.769C192.6 204.926 204.657 192.888 213.491 178.954L219.178 182.563C209.806 197.334 197.036 210.093 182.236 219.456ZM55.2156 219.437C40.4446 210.074 27.6751 197.305 18.3025 182.514L23.9896 178.906C32.8329 192.869 44.8807 204.916 58.8241 213.75L55.2156 219.437ZM223.98 174.268L218.014 171.131C225.789 156.369 230.071 140.357 230.764 123.556V123.344L237.5 123.613V123.825C236.759 141.637 232.217 158.611 223.98 174.268ZM13.5104 174.239C5.24442 158.525 0.702463 141.56 0 123.806L6.72633 123.536C7.3903 140.29 11.6724 156.292 19.4669 171.102L13.5104 174.239ZM6.73595 114.501L0 114.231V114.125C0.731332 96.352 5.26366 79.4159 13.4719 63.7789L19.438 66.9063C11.7013 81.6484 7.42879 97.6222 6.73595 114.395V114.501ZM230.764 114.327C230.09 97.5837 225.789 81.581 217.975 66.7716L223.932 63.6249C232.207 79.3293 236.769 96.2943 237.49 114.048L230.764 114.318V114.327ZM23.9511 59.1022L18.264 55.5033C27.6174 40.7227 40.3868 27.9532 55.1674 18.571L58.776 24.2581C44.8229 33.111 32.7752 45.1588 23.9511 59.1022ZM213.453 58.9675C204.59 45.0144 192.542 32.9763 178.589 24.1619L182.188 18.4652C196.969 27.8185 209.748 40.5687 219.14 55.3589L213.453 58.9675ZM66.5897 19.7257L63.4527 13.7692C79.1474 5.49364 96.1124 0.942067 113.876 0.22998L114.145 6.95631C97.4018 7.6299 81.3991 11.9217 66.5897 19.7161V19.7257ZM170.766 19.6391C156.033 11.912 140.079 7.64915 123.326 6.96593L123.422 0.22998H123.643C141.349 0.961312 158.276 5.48402 173.893 13.6826L170.766 19.6488V19.6391Z"
              fill="#CCCCCC"
            />
          </g>
          {/* End: Inner circle with dashed lines */}

          {/* Start: Top Arrow line */}
          <g
            transform={`rotate(${currentRotationInAngles}, ${rotationCenter[0]}, ${rotationCenter[1]})`}
          >
            <path
              d="M233.653 55.9822L233.653 88.1621"
              stroke="#37A851"
              strokeWidth="3"
              strokeMiterlimit="10"
            />
            <path
              d="M243.653 54.7879L223.653 54.9824L233.454 38.1624L243.653 54.7879Z"
              fill="#CCEED2"
              stroke="#37A851"
              strokeWidth="3"
              strokeLinejoin="round"
            />
            {/* End: Top Arrow line */}

            {/* Start: Top Arrow draggable area */}
            <rect
              style={{ userSelect: "none" }}
              visibility={
                isPlayerInProgress ? Visibility.Hidden : Visibility.Visible
              }
              width="700"
              height="800"
              transform={`translate(-100, -200)`}
              fill="transparent"
              opacity="0.4"
              onMouseDown={() => {
                startClickOrTouch();
              }}
              onTouchStart={() => {
                startClickOrTouch();
              }}
              onMouseMove={(e) => {
                if (arrowDraggable) {
                  moveWhileClickOrTouch(e.clientX, e.clientY);
                }
              }}
              onTouchMove={(e) => {
                if (
                  arrowDraggable &&
                  e.touches !== null &&
                  e.touches.length > 0
                ) {
                  const touchInstance = e.touches[0];
                  moveWhileClickOrTouch(
                    touchInstance.clientX,
                    touchInstance.clientY
                  );
                }
              }}
              onMouseUp={(e) => {
                endClickOrTouch(e, e.clientX, e.clientY);
              }}
              onTouchEnd={(e) => {
                if (
                  e.nativeEvent !== null &&
                  e.nativeEvent.changedTouches !== null
                ) {
                  // Retrieves the touch instance from the nativeEvent element (changedTouches).
                  // Although the current touch has ended, this object retains information about inactive touches.
                  const touchInstance = e.nativeEvent.changedTouches[0];
                  endClickOrTouch(
                    e,
                    touchInstance.clientX,
                    touchInstance.clientY
                  );
                }
              }}
              onMouseOut={(e) => {
                cancelClickOrTouch(e);
              }}
              onTouchCancel={(e) => {
                cancelClickOrTouch(e);
              }}
            ></rect>
            {/* End: Top Arrow draggable area */}

            {/* Start: Baby Birth Position Image */}
            <g
              transform={`translate(${babyHeadPositionX}, ${babyHeadPositionY})`}
            >
              <BabyBirthPosition
                position={babyHeadPosition}
              ></BabyBirthPosition>
            </g>
            {/* End: Baby Birth Position Image */}

            {/* Start: Dilation Cervix Image */}
            <g transform="translate(124, 88)">
              <CervixDilation value={cervixOpening}></CervixDilation>
            </g>
            {/* End: Dilation Cervix Image */}

            {/* Start: Position label (angle range <= 90 or >= 270 degrees) */}
            {/* This one will show and rotate when the angle is smaller or equal than 90 degrees or greater or equal than 270
                The reason for having this showing only in that angle span is because it should keep showing the text in a readable
                way. In the other angle span, the text will also rotate even more and then it is difficult to read. There is going
                to be a different label for this specific case 
            */}
            <text
              fill="#37A851"
              visibility={getTopRotationLabelVisibility()}
              fontFamily="Lato"
              fontSize="20"
              fontWeight="900"
              pointerEvents="none"
              textAnchor="end"
              x={
                getRotationLabelByAngle() &&
                getRotationLabelByAngle().length === 2
                  ? "247"
                  : "253"
              }
              y="30"
              style={{ userSelect: "none" }}
            >
              <tspan>{getRotationLabelByAngle()}</tspan>
            </text>
            {/* End: Position label (angle range <= 90 or >= 270 degrees) */}
          </g>

          {/* Start: Position label (angle range > 90 or < 270 degrees) */}
          {/* This one will show and rotate when the angle is between 90 and 270 degrees (not including them)
              The reason for having this showing only in that angle span is because it should keep showing the text in a readable
              way (with the proper rotation) within the label
          */}
          <g
            transform={`rotate(${currentRotationInAngles - 180}, ${
              rotationCenter[0]
            }, ${rotationCenter[1]})`}
          >
            <text
              fill="#37A851"
              visibility={getBottomRotationLabelVisibility()}
              fontFamily="Lato"
              fontSize="20"
              fontWeight="900"
              pointerEvents="none"
              textAnchor="end"
              x={
                getRotationLabelByAngle() &&
                getRotationLabelByAngle().length === 2
                  ? "250"
                  : "256"
              }
              y="385"
              style={{ userSelect: "none" }}
            >
              <tspan>{getRotationLabelByAngle()}</tspan>
            </text>
          </g>
          {/* End: Position label (angle range > 90 or < 270 degrees) */}
        </g>
      </svg>
    </div>
  );
};

export default RotationAndOpening;
